import React, { useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import type { Profile } from '../types/database';
import type { Session, User as SupabaseUser } from '@supabase/supabase-js';

export interface User {
  id: string;
  username: string;
  displayName: string;
  email: string;
  bio: string;
  avatar?: string;
  coverUrl?: string;
  isSeller: boolean;
  sellerRating?: number;
  followers: number;
  following: number;
  verified: boolean;
  isAdmin: boolean;
  badges: string[];
  styleTags: string[];
  reputationScore: number;
  availabilityStatus: 'online' | 'busy' | 'away' | 'offline' | 'open_to_chat';
  location: string;
  website: string;
  pronouns: string;
  favoriteBrands: string[];
  shoeSize: string;
  // Identity System
  level: number;
  xpPoints: number;
  cultureScore: number;
  cultureTitle: string;
  styleArchetype: string;
  reputationTier: string;
  profileTheme: string;
  accountType: 'seller' | 'collector' | 'curator' | 'creator';
  activeStreak: number;
  totalSales: number;
  // Personal Story
  myStory: string;
  originStory: string;
  inspiration: string;
  stylePhilosophy: string;
  currentChapter: string;
  motto: string;
  whatD88Means: string;
  futureVision: string;
  favoriteQuote: string;
  // Goals + Dreams
  goals: string[];
  dreamBrand: string;
  dreamSneaker: string;
  dreamGrail: string;
  currentProject: string;
  biggestAmbition: string;
  dreamCollab: string;
  // Style Identity
  outfitAesthetic: string;
  mostWornPiece: string;
  signatureLook: string;
  favoriteColors: string[];
  styleEvolution: string;
  // Personality + Culture
  creativeInterests: string[];
  culturalInfluences: string;
  favoriteCity: string;
  playlistUrl: string;
  favoriteEra: string;
  communityValues: string;
  whatIStandFor: string;
  // Social + Connection
  socialLinks: Record<string, string>;
  openToCollab: boolean;
  openToBusiness: boolean;
  languages: string[];
  networkingInterests: string[];
  currentlyChasing: string;
  fitOfMonth: string;
  emailVerified: boolean;
}

export interface AuthContextType {
  user: User | null;
  session: Session | null;
  isLoggedIn: boolean;
  loading: boolean;
  signUp: (email: string, password: string, username: string, displayName: string, accountType?: 'seller' | 'collector' | 'curator' | 'creator') => Promise<{ error: string | null }>;
  signIn: (email: string, password: string) => Promise<{ error: string | null }>;
  signOut: () => Promise<void>;
  updateProfile: (updates: Partial<Profile>) => Promise<{ error: string | null }>;
  refreshProfile: () => Promise<void>;
  setAvailability: (status: User['availabilityStatus']) => Promise<void>;
  checkUsernameAvailable: (username: string) => Promise<boolean>;
}

function mapProfileToUser(profile: Profile): User {
  const p = profile as any;
  return {
    id: p.id,
    username: p.username,
    displayName: p.display_name,
    email: p.email,
    bio: p.bio || '',
    avatar: p.avatar_url || undefined,
    coverUrl: p.cover_url || undefined,
    isSeller: p.is_seller,
    sellerRating: p.seller_rating ?? undefined,
    followers: p.followers_count,
    following: p.following_count,
    verified: p.verified,
    isAdmin: p.is_admin ?? false,
    badges: (p.badges as string[]) || [],
    styleTags: (p.style_tags as string[]) || [],
    reputationScore: p.reputation_score || 0,
    availabilityStatus: (p.availability_status as User['availabilityStatus']) || 'offline',
    location: p.location || '',
    website: p.website || '',
    pronouns: p.pronouns || '',
    favoriteBrands: (p.favorite_brands as string[]) || [],
    shoeSize: p.shoe_size || '',
    // Identity System
    level: p.level || 1,
    xpPoints: p.xp_points || 0,
    cultureScore: p.culture_score || 0,
    cultureTitle: p.culture_title || 'Observer',
    styleArchetype: p.style_archetype || '',
    reputationTier: p.reputation_tier || 'Standard',
    profileTheme: p.profile_theme || 'default',
    accountType: p.account_type || 'collector',
    activeStreak: p.active_streak || 0,
    totalSales: p.total_sales || 0,
    // Personal Story
    myStory: p.my_story || '',
    originStory: p.origin_story || '',
    inspiration: p.inspiration || '',
    stylePhilosophy: p.style_philosophy || '',
    currentChapter: p.current_chapter || '',
    motto: p.motto || '',
    whatD88Means: p.what_d88_means || '',
    futureVision: p.future_vision || '',
    favoriteQuote: p.favorite_quote || '',
    // Goals + Dreams
    goals: (p.goals as string[]) || [],
    dreamBrand: p.dream_brand || '',
    dreamSneaker: p.dream_sneaker || '',
    dreamGrail: p.dream_grail || '',
    currentProject: p.current_project || '',
    biggestAmbition: p.biggest_ambition || '',
    dreamCollab: p.dream_collab || '',
    // Style Identity
    outfitAesthetic: p.outfit_aesthetic || '',
    mostWornPiece: p.most_worn_piece || '',
    signatureLook: p.signature_look || '',
    favoriteColors: (p.favorite_colors as string[]) || [],
    styleEvolution: p.style_evolution || '',
    // Personality + Culture
    creativeInterests: (p.creative_interests as string[]) || [],
    culturalInfluences: p.cultural_influences || '',
    favoriteCity: p.favorite_city || '',
    playlistUrl: p.playlist_url || '',
    favoriteEra: p.favorite_era || '',
    communityValues: p.community_values || '',
    whatIStandFor: p.what_i_stand_for || '',
    // Social + Connection
    socialLinks: (p.social_links as Record<string, string>) || {},
    openToCollab: p.open_to_collab || false,
    openToBusiness: p.open_to_business || false,
    languages: (p.languages as string[]) || [],
    networkingInterests: (p.networking_interests as string[]) || [],
    currentlyChasing: p.currently_chasing || '',
    fitOfMonth: p.fit_of_month || '',
    emailVerified: false,
  };
}

export const AuthContext = React.createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = React.useState<User | null>(null);
  const [session, setSession] = React.useState<Session | null>(null);
  const [loading, setLoading] = React.useState(true);

  const fetchProfile = useCallback(async (userId: string, supabaseUser?: SupabaseUser): Promise<User | null> => {
    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', userId)
      .single();

    if (error || !data) {
      console.error('Error fetching profile:', error);
      return null;
    }
    const mapped = mapProfileToUser(data as Profile);
    mapped.emailVerified = !!supabaseUser?.email_confirmed_at;
    return mapped;
  }, []);

  useEffect(() => {
    let settled = false;
    const settle = () => { if (!settled) { settled = true; setLoading(false); } };

    const timeout = setTimeout(() => {
      console.warn('Auth init timed out — continuing as guest');
      settle();
    }, 1000);

    supabase.auth.getSession()
      .then(async ({ data: { session: currentSession } }) => {
        setSession(currentSession);
        if (currentSession?.user) {
          try {
            const profile = await fetchProfile(currentSession.user.id, currentSession.user);
            setUser(profile);
          } catch {}
        }
        settle();
      })
      .catch((err) => {
        console.warn('Auth session retrieval failed, continuing as guest:', err?.message);
        settle();
      });

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, newSession) => {
        setSession(newSession);
        if (newSession?.user) {
          try {
            const profile = await fetchProfile(newSession.user.id, newSession.user);
            setUser(profile);
          } catch {}
        } else {
          setUser(null);
        }
        settle();
      }
    );

    return () => { clearTimeout(timeout); subscription.unsubscribe(); };
  }, [fetchProfile]);

  const signUp = async (
    email: string,
    password: string,
    username: string,
    displayName: string,
    accountType: 'seller' | 'collector' | 'curator' | 'creator' = 'collector'
  ): Promise<{ error: string | null }> => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          username,
          display_name: displayName,
        },
      },
    });

    if (error) return { error: error.message };

    if (data.user) {
      const isSeller = accountType === 'seller';
      const roleBadge = accountType === 'seller' ? 'Seller'
        : accountType === 'curator' ? 'Curator'
        : accountType === 'creator' ? 'Creator'
        : 'Collector';

      await supabase.from('profiles').update({
        username,
        display_name: displayName,
        avatar_url: `https://ui-avatars.com/api/?name=${encodeURIComponent(displayName)}&background=random&color=fff&size=200`,
        account_type: accountType,
        is_seller: isSeller,
        badges: [roleBadge],
      }).eq('id', data.user.id);

      if (isSeller) {
        await supabase.from('seller_rep_scores').insert({
          seller_id: data.user.id,
          overall_score: 0,
          tier: 'Bronze',
        });
      }
    }

    return { error: null };
  };

  const signIn = async (
    email: string,
    password: string
  ): Promise<{ error: string | null }> => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    if (error) return { error: error.message };
    return { error: null };
  };

  const signOut = async () => {
    await supabase.auth.signOut();
    setUser(null);
    setSession(null);
  };

  const updateProfile = async (
    updates: Partial<Profile>
  ): Promise<{ error: string | null }> => {
    if (!session?.user) return { error: 'Not authenticated' };

    const { error } = await supabase
      .from('profiles')
      .update(updates)
      .eq('id', session.user.id);

    if (error) return { error: error.message };

    // Refresh local user state
    const profile = await fetchProfile(session.user.id);
    if (profile) setUser(profile);
    return { error: null };
  };

  const refreshProfile = async () => {
    if (session?.user) {
      const profile = await fetchProfile(session.user.id);
      if (profile) setUser(profile);
    }
  };

  const setAvailability = async (status: User['availabilityStatus']) => {
    if (!session?.user) return;
    await supabase.from('profiles').update({ availability_status: status }).eq('id', session.user.id);
    setUser(prev => prev ? { ...prev, availabilityStatus: status } : null);
  };

  const checkUsernameAvailable = async (username: string): Promise<boolean> => {
    if (!username || username.length < 3) return false;
    const { data } = await supabase
      .from('profiles')
      .select('id')
      .eq('username', username.toLowerCase())
      .neq('id', session?.user?.id || '')
      .maybeSingle();
    return !data;
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        session,
        isLoggedIn: !!user,
        loading,
        signUp,
        signIn,
        signOut,
        updateProfile,
        refreshProfile,
        setAvailability,
        checkUsernameAvailable,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = React.useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
